import React, {useLayoutEffect, useRef, useState} from 'react'
import {CardTypeEnum, type CardStateOrdered, CardEnum} from '@colonist/game-states'
import {UIGameCardStack} from '../UIGameCardStack/UIGameCardStack.tsx'
import styles from './UIGameProgressCardTooltipBody.module.scss'
import {CardValidatorOrdered} from '@colonist/game-mechanic-validators'
import clsx from 'clsx'
import {getBankCardStackSize} from '../../core/UIGameBank/getBankCardStackSize.ts'
import {type ModeSettingEnum} from '@colonist/types'

const PROGRESS_TYPES_AND_BACKS = [
    {cardType: CardTypeEnum.ProgressScience, cardBack: CardEnum.ProgressCardScienceBack},
    {cardType: CardTypeEnum.ProgressTrade, cardBack: CardEnum.ProgressCardTradeBack},
    {cardType: CardTypeEnum.ProgressPolitics, cardBack: CardEnum.ProgressCardPoliticsBack},
]

export interface UIGameProgressCardTooltipBodyProps {
    progressCards: CardStateOrdered
    modeSetting: ModeSettingEnum
    hideBadge?: boolean
}

export const UIGameProgressCardTooltipBody = ({progressCards, modeSetting, hideBadge}: UIGameProgressCardTooltipBodyProps) => {
    const progressCardsValidator = new CardValidatorOrdered({cards: progressCards.cards})
    const cardCounts = PROGRESS_TYPES_AND_BACKS.map(({cardType, cardBack}) => ({
        cardType: cardType,
        count: progressCardsValidator.getCardCountOfType(cardType),
        cardBack: cardBack,
    }))
    const containerRef = useRef<HTMLDivElement>(null)
    const [dynamicGap, setDynamicGap] = useState(-43)

    useLayoutEffect(() => {
        const tooltipMenuElement = containerRef.current
        if(!tooltipMenuElement) return

        const calculateGapBetweenCards = () => {
            const cardElement = tooltipMenuElement.querySelector('[data-card-enum]')
            if(!cardElement) return
            const cardElementHeight = cardElement.getBoundingClientRect().height
            const gapPx = cardElementHeight * 0.9
            setDynamicGap(-gapPx)
        }

        calculateGapBetweenCards()

        const resizeObserver = new ResizeObserver(() => calculateGapBetweenCards())
        resizeObserver.observe(tooltipMenuElement)

        return () => resizeObserver.disconnect()
    }, [])

    return (
        <div className={styles.row} ref={containerRef}>
            {cardCounts.map(({cardType, count, cardBack}) => {
                const cardCount = count
                const stackMax = getBankCardStackSize(modeSetting, cardType, cardCount)
                const isOutOfCards = count == 0

                return (
                    <UIGameCardStack
                        cardEnum={cardBack}
                        className={clsx({
                            [styles.outOfCardsTint]: isOutOfCards,
                        })}
                        count={count}
                        gap={dynamicGap}
                        hideBadge={hideBadge ?? false}
                        key={cardType}
                        showEmptyCard={isOutOfCards}
                        stackDirection="vertical"
                        stackMax={stackMax}
                    />
                )
            })}
        </div>
    )
}
